import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdChat, MdMic, MdMicOff } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import styles from "./VoicePopup.module.css";

const SAMPLE_RATE = 48000;
const CHUNK_MS = 100;
const AUDIO_THRESHOLD = 0.003;
const SILENCE_DURATION = 800;

function floatTo16BitPCM(float32Array) {
  const length = float32Array.length;
  const buffer = new ArrayBuffer(length * 2);
  const view = new DataView(buffer);

  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return buffer;
}

class AudioPlayer {
  constructor() {
    this.audioContext = null;
    this.queue = [];
    this.nextTime = 0;
    this.isPlaying = false;
  }

  async init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
    }
  }

  async playAudio(arrayBuffer) {
    await this.init();

    const int16Array = new Int16Array(arrayBuffer);
    const float32Array = new Float32Array(int16Array.length);

    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768;
    }

    this.queue.push(float32Array);
    this.schedule();
  }

  schedule() {
    if (!this.audioContext) return;

    if (!this.isPlaying) {
      this.nextTime = Math.max(
        this.audioContext.currentTime,
        this.nextTime || 0
      );
      this.isPlaying = true;
    }

    while (this.queue.length > 0) {
      const chunk = this.queue.shift();
      const audioBuffer = this.audioContext.createBuffer(
        1,
        chunk.length,
        this.audioContext.sampleRate
      );
      audioBuffer.getChannelData(0).set(chunk);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      source.start(this.nextTime);

      this.nextTime += audioBuffer.length / this.audioContext.sampleRate;
    }

    if (this.queue.length === 0) {
      this.isPlaying = false;
    }
  }

  clear() {
    this.queue = [];
    if (this.audioContext) {
      this.nextTime = this.audioContext.currentTime;
    }
  }
}

const VoicePopup = ({ selectedAgent }) => {
  const [isActive, setIsActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);
  const [conversationLog, setConversationLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isChatMode, setIsChatMode] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sessionId] = useState(uuidv4()); // Unique session ID for chat
  const prevAgentIdRef = useRef(null); // Track previous agentId

  const wsRef = useRef(null);
  const playerRef = useRef(new AudioPlayer());
  const micRef = useRef({
    stream: null,
    audioContext: null,
    processor: null,
    source: null,
    running: false,
  });

  const addToLog = useCallback((text, speaker) => {
    setConversationLog((prev) => [
      ...prev,
      {
        id: Date.now(),
        text,
        speaker,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
  }, []);

  const startVoiceChat = async () => {
    try {
      if (!selectedAgent?.agentId) {
        setError("No agent selected.");
        return;
      }
      setError(null);
      console.log(
        "🚀 Starting voice chatbot for agentId:",
        selectedAgent.agentId
      );
      const response = await fetch(
        "https://prod.toxboxai.com/api/start-voice",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agentId: selectedAgent.agentId }), // Send agentId
        }
      );

      if (!response.ok) {
        throw new Error("Failed to start voice chat");
      }

      const ws = new WebSocket("ws://localhost:8081");
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        console.log("✅ Voice chat connected");
        setIsConnected(true);
        addToLog("Voice chat started! Speak to me.", "system");
      };

      ws.onmessage = async (event) => {
        try {
          if (typeof event.data === "string") {
            const data = JSON.parse(event.data);

            if (data.source === "server" && data.event === "call_ended") {
              // Handle call end (manual, agent change, or inactivity)
              console.log("🛑 Voice chat ended by server");
              stopVoiceChat();
              addToLog("Voice chat ended.", "system");
              return;
            }

            if (
              data.source === "Provaantech" &&
              data.event?.type === "transcript"
            ) {
              if (data.event.speaker === "user") {
                addToLog(data.event.text, "user");
              } else if (data.event.speaker === "agent") {
                addToLog(data.event.text, "bot");
              }
            }

            if (
              data.source === "Provaantech" &&
              data.event?.type === "playback_clear_buffer"
            ) {
              playerRef.current.clear();
              setIsListening(false);
            }
          } else {
            const arrayBuffer = event.data;
            if (arrayBuffer.byteLength > 0) {
              await playerRef.current.playAudio(arrayBuffer);
            }
          }
        } catch (error) {
          console.error("Message handling error:", error);
        }
      };

      ws.onclose = () => {
        console.log("❌ Voice chat disconnected");
        setIsConnected(false);
        addToLog("Voice chat disconnected.", "system");
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setError("Connection error");
      };

      wsRef.current = ws;

      await startMicrophone();
      setIsActive(true);
    } catch (error) {
      console.error("Failed to start voice chat:", error);
      setError(error.message);
    }
  };

  const startMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      let chunkBuffers = [];
      const chunkSamplesTarget = Math.round(
        (CHUNK_MS / 1000) * audioContext.sampleRate
      );
      let lastSpeechTime = 0;
      let lastCommitTime = 0;

      processor.onaudioprocess = (event) => {
        if (!micRef.current.running) return;

        const inputData = event.inputBuffer.getChannelData(0);
        const rms = Math.sqrt(
          inputData.reduce((sum, sample) => sum + sample * sample, 0) /
          inputData.length
        );

        setAudioLevel(rms);

        const isSpeechDetected = rms > AUDIO_THRESHOLD;

        if (isSpeechDetected) {
          lastSpeechTime = Date.now();
          if (!isSpeaking) {
            setIsSpeaking(true);
            setIsListening(false);
          }
        }

        const copy = new Float32Array(inputData.length);
        copy.set(inputData);
        chunkBuffers.push(copy);

        const totalSamples = chunkBuffers.reduce(
          (sum, buffer) => sum + buffer.length,
          0
        );

        if (totalSamples >= chunkSamplesTarget) {
          const merged = new Float32Array(totalSamples);
          let offset = 0;

          for (const buffer of chunkBuffers) {
            merged.set(buffer, offset);
            offset += buffer.length;
          }

          chunkBuffers = [];
          const pcmBuffer = floatTo16BitPCM(merged);

          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(pcmBuffer);
          }

          const now = Date.now();
          const timeSinceLastSpeech = now - lastSpeechTime;

          if (
            isSpeaking &&
            timeSinceLastSpeech > SILENCE_DURATION &&
            now - lastCommitTime > 500
          ) {
            lastCommitTime = now;
            setIsSpeaking(false);
            setIsListening(true);

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "commit" }));
            }
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      micRef.current = {
        stream,
        audioContext,
        processor,
        source,
        running: true,
      };

      console.log("✅ Microphone started");
    } catch (error) {
      console.error("Microphone failed:", error);
      setError("Microphone access denied. Please allow microphone access.");
    }
  };

  const stopVoiceChat = async () => {
    try {
      if (micRef.current.running) {
        const { stream, audioContext, processor, source } = micRef.current;

        if (processor) processor.disconnect();
        if (source) source.disconnect();
        if (stream) stream.getTracks().forEach((track) => track.stop());
        if (audioContext) audioContext.close();

        micRef.current.running = false;
      }

      playerRef.current.clear();

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      await fetch(
        "https://prod.toxboxai.com/api/end-voice",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}), // No body needed for end-voice
        }
      );

      setIsActive(false);
      setIsConnected(false);
      setIsSpeaking(false);
      setIsListening(false);
      setAudioLevel(0);
      setError(null);

      console.log("🛑 Voice chat stopped");
    } catch (error) {
      console.error("Error stopping voice chat:", error);
      setError("Error stopping voice chat");
    }
  };

  // Monitor selectedAgent changes to restart voice chat
  useEffect(() => {
    if (
      isActive &&
      selectedAgent?.agentId &&
      selectedAgent.agentId !== prevAgentIdRef.current
    ) {
      console.log("🔄 Agent changed, restarting voice chat");
      stopVoiceChat().then(() => {
        startVoiceChat();
      });
    }
    prevAgentIdRef.current = selectedAgent?.agentId;
  }, [selectedAgent?.agentId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    if (!selectedAgent?.agentId) {
      setMessages([
        ...messages,
        { text: "Error: No agent selected.", sender: "bot" },
      ]);
      return;
    }

    const userMessage = { text: inputMessage, sender: "user" };
    setMessages([...messages, userMessage]);
    setInputMessage("");

    try {
      const response = await axios.post(
        "https://prod.toxboxai.com/api/chat",
        {
          prompt: inputMessage,
          agentId: selectedAgent.agentId,
          sessionId,
        }
      );
      console.log("hfekwhhkrughekrhkrhhhkuruhkghkarhrhrkh", selectedAgent);
      const aiMessage = { text: response.data.response, sender: "bot" };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        { text: "Error: Could not get response from AI.", sender: "bot" },
      ]);
    }
  };

  return (
    <div
      className={styles.chatbotContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.chatbotWrapper}>
        <div className={styles.contentWrapper}>
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
            }}
          >
            {isConnected ? (
              <div
                style={{
                  borderRadius: "50%",
                  height: "10px",
                  width: "10px",
                  background: isSpeaking
                    ? "#4CAF50"
                    : isListening
                      ? "#2196F3"
                      : "#666",
                }}
              ></div>
            ) : (
              <div
                style={{
                  borderRadius: "50%",
                  height: "10px",
                  width: "10px",
                  background: "#ff2727ff",
                }}
              ></div>
            )}
          </div>
          <div className={styles.imageWrapper}>
            <img
              src={selectedAgent?.imageUrl}
              alt="Avatar"
              className={styles.avatarImage}
            />
          </div>
          <div
            className={`${styles.nameWrapper} ${isHovered ? styles.visible : ""
              }`}
          >
            <p className={styles.agentName}>
              Chat with {selectedAgent?.fullName}
            </p>
          </div>
          {!isChatMode ? (
            <button
              className={`${styles.micButton} ${isMicOn && !isChatMode ? styles.pulse : ""
                }`}
              onClick={() => !isChatMode && setIsMicOn(!isMicOn)}
            >
              <div className={styles.waveCircleWrapper}>
                {isMicOn && !isChatMode && (
                  <div className={styles.waveAnimation}></div>
                )}
              </div>
              <div className={styles.micIcon}>
                {!isChatMode && (
                  <>
                    {isMicOn ? (
                      <MdMic
                        className={styles.micIconOverlay}
                        onClick={startVoiceChat}
                      />
                    ) : (
                      <MdMicOff
                        className={styles.micIconOverlay}
                        onClick={stopVoiceChat}
                      />
                    )}
                  </>
                )}
              </div>
            </button>
          ) : null}
          <button
            className={styles.modeToggleButton}
            onClick={() => setIsChatMode(!isChatMode)}
          >
            {isChatMode ? (
              <MdMic className={styles.modeIcon} />
            ) : (
              <MdChat className={styles.modeIcon} />
            )}
          </button>
        </div>
        {isChatMode && (
          <div className={styles.chatPopup}>
            <div className={styles.chatHeader}>
              <span className={styles.headerAgentName}>
                {selectedAgent?.fullName || "Agent"}
              </span>
            </div>
            <div className={styles.chatMessages}>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`${styles.message} ${msg.sender === "user"
                    ? styles.userMessage
                    : styles.agentMessage
                    }`}
                >
                  {msg.text}
                </div>
              ))}
            </div>
            <div className={styles.chatInputWrapper}>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                className={styles.chatInput}
                placeholder="Type your message..."
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              />
              <button onClick={handleSendMessage} className={styles.sendButton}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoicePopup;
